// Display constants for the lead module. Keep the lookup tables here so
// columns / cards / forms agree on labels and ordering.

import type { CustomerSource, LeadStatus } from "@/features/lead/types";

/** Canonical column ordering for the kanban (NEW → LOST). */
export const LEAD_STATUSES: readonly LeadStatus[] = [
  "NEW",
  "CONTACTED",
  "VIEWING_SCHEDULED",
  "NEGOTIATING",
  "WON",
  "LOST",
] as const;

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: "Mới",
  CONTACTED: "Đã liên hệ",
  VIEWING_SCHEDULED: "Hẹn xem",
  NEGOTIATING: "Đàm phán",
  WON: "Chốt deal",
  LOST: "Thất bại",
};

/** One-line descriptions surfaced in the mobile status-change sheet
 *  (and anywhere a status needs more context than the bare label).
 *  Tracks the design's row copy on `playground/laed detail.html`. */
export const LEAD_STATUS_DESCRIPTIONS: Record<LeadStatus, string> = {
  NEW: "Vừa được thêm, chưa liên hệ",
  CONTACTED: "Đã gọi/nhắn lần đầu",
  VIEWING_SCHEDULED: "Đã đặt lịch xem nhà",
  NEGOTIATING: "Đang chốt giá / điều kiện",
  WON: "Đã ký hợp đồng",
  LOST: "Khách rút, không phù hợp",
};

/** Soft-tone class strings for the lead status Badge. Stays in the same
 *  hue family as {@link LEAD_STATUS_DOT_COLORS} but at a much lower
 *  saturation so the badge does not compete with the column dot or the
 *  funnel bar. */
export const LEAD_STATUS_COLORS: Record<LeadStatus, string> = {
  NEW: "border-transparent bg-slate-100 text-slate-700",
  CONTACTED: "border-transparent bg-indigo-100 text-indigo-700",
  VIEWING_SCHEDULED: "border-transparent bg-amber-100 text-amber-700",
  NEGOTIATING: "border-transparent bg-indigo-200 text-indigo-800",
  WON: "border-transparent bg-emerald-100 text-emerald-700",
  LOST: "border-transparent bg-rose-100 text-rose-700",
};

/** Saturated dot/funnel-bar colours, shared by the column header dot and
 *  the page-level funnel bar so the two channels can never drift apart.
 *  Tuned against the design comp at
 *  `design/src/components/desktop/Leads.jsx` (line 41 + 52). LOST is rose
 *  (not zinc) so an actually-populated LOST column is unambiguously
 *  distinct from NEW. */
export const LEAD_STATUS_DOT_COLORS: Record<LeadStatus, string> = {
  NEW: "bg-slate-400",
  CONTACTED: "bg-indigo-300",
  VIEWING_SCHEDULED: "bg-amber-400",
  NEGOTIATING: "bg-indigo-600",
  WON: "bg-emerald-600",
  LOST: "bg-rose-500",
};

export const CUSTOMER_SOURCES: readonly CustomerSource[] = [
  "WALK_IN",
  "FACEBOOK",
  "ZALO",
  "REFERRAL",
  "OTHER",
] as const;

export const CUSTOMER_SOURCE_LABELS: Record<CustomerSource, string> = {
  WALK_IN: "Khách vãng lai",
  FACEBOOK: "Facebook",
  ZALO: "Zalo",
  REFERRAL: "Giới thiệu",
  OTHER: "Khác",
};

/** Sentinel value used by the kanban filter Select for "no filter". */
export const ALL_BROKERS_VALUE = "__ALL_BROKERS__";
